import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { register, googleSignup } from './UserFunctions';
import { GoogleLogin } from '@react-oauth/google';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TNCModal from './TNCModal';
import PrivacyModal from './PrivacyModal';
import moment from 'moment';
import axios from 'axios';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Link as MuiLink,
  Checkbox,
  FormControlLabel,
  Paper
} from '@mui/material';
import { 
  Email,
  Lock,
  Person,
  ArrowForward
} from '@mui/icons-material';

const version = "v0.1";
const abstractApi = "https://ipgeolocation.abstractapi.com/v1/?api_key=" + process.env.REACT_APP_API_ABSTRACT_KEY;

class SignUpForm extends Component {
  constructor() {
    super();
    this.state = {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      acceptedTNC: false,
      tncModalOpen: false,
      privacyModalOpen: false,
      acceptedTNCFromIP: ''
    };
  }

  handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState({
      [name]: type === 'checkbox' ? checked : value
    });
  };

  handleTNCModalOpen = () => {
    this.setState(prev => ({ tncModalOpen: !prev.tncModalOpen }));
  };

  handlePrivacyModalOpen = () => {
    this.setState(prev => ({ privacyModalOpen: !prev.privacyModalOpen }));
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { firstName, lastName, email, password } = this.state;

    if (!firstName || !lastName || !email || !password) {
      toast.error("All fields are required.");
      return;
    }

    // Password validation - must match backend requirements
    const passwordErrors = [];
    if (password.length < 8) {
      passwordErrors.push("at least 8 characters");
    }
    if (!/[A-Z]/.test(password)) {
      passwordErrors.push("one uppercase letter");
    }
    if (!/[a-z]/.test(password)) {
      passwordErrors.push("one lowercase letter");
    }
    if (!/[0-9]/.test(password)) {
      passwordErrors.push("one number");
    }

    if (passwordErrors.length > 0) {
      toast.error(`Password must contain: ${passwordErrors.join(", ")}`);
      return;
    }

    try {
      const location = await axios.get(abstractApi);
      const userData = {
        firstName,
        lastName,
        email,
        password,
        acceptedTNC: this.state.acceptedTNC,
        timeTNCAccepted: moment(),
        acceptedTNCVersion: version,
        acceptedTNCFromIP: location.data.ip_address
      };

      const res = await register(userData);
      toast.success('User Registered. Verification email sent. Redirecting...');
      setTimeout(() => this.props.history.push("/verify"), 3000);
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Registration failed";
      toast.error(errorMsg);
      if (errorMsg.includes("already exists")) {
        setTimeout(() => this.props.history.push("/sign-in"), 3000);
      }
    }
  };

  // "Sign up with Google". Google has already verified the address, so the
  // backend marks the account confirmed and returns a session token straight
  // away — no verification email and no /verify detour.
  handleGoogleSuccess = async (credentialResponse) => {
    if (!this.state.acceptedTNC) {
      toast.error('Please accept the Terms and Conditions first.');
      return;
    }
    try {
      // Same consent evidence the password signup records.
      let ip = '';
      try {
        const location = await axios.get(abstractApi);
        ip = location.data.ip_address;
      } catch (_) { /* consent still recorded; IP best-effort */ }

      const data = await googleSignup({
        credential: credentialResponse.credential,
        acceptedTNC: true,
        timeTNCAccepted: moment(),
        acceptedTNCVersion: version,
        acceptedTNCFromIP: ip,
      });

      const { token, id, email: backendEmail, message } = data || {};
      if (!token) {
        toast.error(message || 'Google sign-up failed.');
        return;
      }

      // Mirrors SignInForm's persistSessionAndRedirect.
      localStorage.setItem("token", token);
      localStorage.setItem("email", backendEmail || this.state.email);
      localStorage.setItem("userId", String(id));
      localStorage.setItem("authenticated", "true");
      toast.success(message || 'Account created!');
      this.props.history.push("/profile/portfolio");
    } catch (err) {
      const msg = err.response?.data?.message || 'Google sign-up failed.';
      toast.error(msg);
      // Already registered → send them to sign in, same as the password path.
      if (msg.includes('already exists')) {
        setTimeout(() => this.props.history.push("/sign-in"), 3000);
      }
    }
  };

  handleGoogleError = () => {
    toast.error('Google sign-up was cancelled or failed.');
  };

  render() {
    const { 
      firstName, 
      lastName, 
      email, 
      password, 
      acceptedTNC,
      tncModalOpen,
      privacyModalOpen
    } = this.state;

    return (
      <Container component="main" maxWidth="xs">
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />

        <Box sx={{ 
          mt: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
          <Paper elevation={3} sx={{ 
            p: 4,
            borderRadius: 2,
            width: '100%',
            boxShadow: 3
          }}>
            <Typography component="h1" variant="h4" align="center" sx={{ mb: 3 }}>
              Create Account
            </Typography>

            <Box component="form" onSubmit={this.handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="First Name"
                name="firstName"
                autoComplete="given-name"
                autoFocus
                value={firstName}
                onChange={this.handleChange}
                InputProps={{
                  startAdornment: (
                    <Person color="action" sx={{ mr: 1 }} />
                  ),
                }}
                sx={{ mb: 2 }}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                label="Last Name"
                name="lastName"
                autoComplete="family-name"
                value={lastName}
                onChange={this.handleChange}
                InputProps={{
                  startAdornment: (
                    <Person color="action" sx={{ mr: 1 }} />
                  ),
                }}
                sx={{ mb: 2 }}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                label="Email Address"
                name="email"
                autoComplete="email"
                value={email}
                onChange={this.handleChange}
                InputProps={{
                  startAdornment: (
                    <Email color="action" sx={{ mr: 1 }} />
                  ),
                }}
                sx={{ mb: 2 }}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={this.handleChange}
                InputProps={{
                  startAdornment: (
                    <Lock color="action" sx={{ mr: 1 }} />
                  ),
                }}
                helperText="Min 8 characters with uppercase, lowercase & number"
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    name="acceptedTNC"
                    checked={acceptedTNC}
                    onChange={this.handleChange}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree with Cryptologics-ai's{' '}
                    <MuiLink 
                      component="span" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.handleTNCModalOpen();
                      }}
                      sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      terms and conditions
                    </MuiLink>{' '}
                    and the{' '}
                    <MuiLink 
                      component="span" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.handlePrivacyModalOpen();
                      }}
                      sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      privacy policy
                    </MuiLink>
                  </Typography>
                }
                sx={{ mt: 1, mb: 2 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={!acceptedTNC}
                sx={{
                  mt: 2,
                  mb: 2,
                  py: 1.5,
                  background: 'linear-gradient(90deg, #3f6fb0 0%, #4d8fc7 100%)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 2
                  }
                }}
                endIcon={<ArrowForward />}
              >
                Sign Up
              </Button>

              {/* Sign up with Google. Gated on the same checkbox as the submit
                  button — GoogleLogin renders a Google-controlled iframe with no
                  usable disabled prop, so we make the wrapper inert instead. */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2 }}>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                <Typography variant="caption" color="text.secondary">or</Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  pointerEvents: acceptedTNC ? 'auto' : 'none',
                  opacity: acceptedTNC ? 1 : 0.5,
                }}
              >
                <GoogleLogin
                  onSuccess={this.handleGoogleSuccess}
                  onError={this.handleGoogleError}
                  text="signup_with"
                />
              </Box>

              {!acceptedTNC && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', textAlign: 'center', mt: 1 }}
                >
                  Accept the Terms and Conditions to continue with Google.
                </Typography>
              )}

              <Typography variant="body2" align="center">
                Already have an account?{' '}
                <MuiLink component={Link} to="/sign-in">
                  Sign in
                </MuiLink>
              </Typography>
            </Box>
          </Paper>
        </Box>

        <TNCModal
          open={tncModalOpen}
          onClose={this.handleTNCModalOpen}
        />
        <PrivacyModal
          open={privacyModalOpen}
          onClose={this.handlePrivacyModalOpen}
        />
      </Container>
    );
  }
}

export default SignUpForm;